import { useQueries } from "@tanstack/react-query";
import type { BankAccountOption } from "../repositories/bankRepository";
import { bankRepository } from "../repositories/bankRepository";

/**
 * Query key helpers
 */
export const bankAccountOptionsQueryKey = () =>
  ["bank-account", "options"] as const;
export const bankAccountQueryKey = (id?: string | null) =>
  ["bank-account", id ?? ""] as const;

/**
 * Single hook that exposes both:
 * - bank account options (list)
 * - a single bank account by id (optional)
 *
 * Usage:
 * const { options, account, isAnyLoading, refetchAll } = useBankAccountQueries(id);
 */
export function useBankAccountQueries(id?: string | null) {
  const queries = useQueries({
    queries: [
      {
        queryKey: bankAccountOptionsQueryKey(),
        queryFn: () => bankRepository.getBankAccountsOptions(),
        enabled: true,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: 2,
        refetchOnMount: "always" as const,
      },
      {
        queryKey: bankAccountQueryKey(id),
        queryFn: () => {
          if (!id) return Promise.resolve(null as BankAccountOption | null);
          return bankRepository.getBankAccount(id);
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
        retry: 2,
        refetchOnMount: "always" as const,
      },
    ],
  });

  const [optionsQuery, accountQuery] = queries;

  return {
    options: (optionsQuery.data ?? []) as BankAccountOption[],
    account: (accountQuery.data ?? null) as BankAccountOption | null,

    isOptionsLoading: optionsQuery.isLoading,
    isAccountLoading: accountQuery.isLoading,
    isAnyLoading: queries.some((q) => q.isLoading),

    optionsError: optionsQuery.error,
    accountError: accountQuery.error,
    hasAnyError: queries.some((q) => q.error),

    refetchAll: () => queries.forEach((q) => q.refetch()),
    refetchOptions: optionsQuery.refetch,
    refetchAccount: accountQuery.refetch,

    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
}
